import {
  BellIcon,
  ChatBubbleLeftRightIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  SparklesIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../contexts/ThemeContext';
import apiService from '../services/api';
import TabNavigation from '../components/TabNavigation';
import clsx from 'clsx';

interface Conversation {
  id: number;
  member_id: number | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  channel: 'sms' | 'email' | 'chat' | 'push_notification' | null;
  last_message: string | null;
  last_sender_type: 'member' | 'employee' | null;
  last_message_at: string | null;
  unread_count: number;
  phone_number: string | null;
  email_address: string | null;
}

interface Message {
  id: number;
  sender_id: number | null;
  sender_name: string;
  sender_type: 'member' | 'employee';
  content: string;
  channel: 'sms' | 'email' | 'chat' | 'push_notification';
  delivery_status: string;
  subject: string | null;
  timestamp: string;
  delivered_at: string | null;
  read_at: string | null;
}

const CommunicationPage: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { gym } = useAuth();
  const { theme } = useTheme();
  usePageTitle('Communication');

  // Main tab state
  const [activeTab, setActiveTab] = useState<'send' | 'history' | 'campaigns'>('send');
  
  // Send Message tab state
  const [sendMode, setSendMode] = useState<'individual' | 'bulk'>('individual');
  const [channel, setChannel] = useState<'email' | 'sms' | 'push_notification'>('email');
  const [selectedMember, setSelectedMember] = useState<number | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [recipientType, setRecipientType] = useState<'all_members' | 'group' | 'selected'>('all_members');
  const [subject, setSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [members, setMembers] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Message History tab state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [replyContent, setReplyContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isReplying, setIsReplying] = useState(false);

  // Load data
  const loadMembers = useCallback(async () => {
    try {
      const response = await apiService.getMembers();
      setMembers(response.members || []);
    } catch (error) {
      console.error('Failed to load members:', error);
    }
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const response = await apiService.getGroups();
      setGroups(response.groups || []);
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const data = await apiService.getConversations();
      setConversations(data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  }, []);

  const loadMessages = useCallback(async (conversationId: number) => {
    setIsLoadingMessages(true);
    try {
      const data = await apiService.getConversationMessages(conversationId);
      setMessages(data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setIsLoading(true);
      await Promise.all([
        loadMembers(),
        loadGroups(),
        loadConversations()
      ]);
      setIsLoading(false);
    };
    loadAll();
  }, [loadMembers, loadGroups, loadConversations]);

  // Auto-refresh conversations every 30 seconds
  useEffect(() => {
    if (activeTab === 'history') {
      const interval = setInterval(() => {
        loadConversations();
        if (selectedConversation) {
          loadMessages(selectedConversation.id);
        }
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab, selectedConversation, loadConversations, loadMessages]);

  // Handle conversation selection
  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation.id);
  };

  // Handle send message
  const handleSendMessage = async () => {
    if (!messageContent.trim()) {
      setSendResult({ type: 'error', message: 'Message content is required' });
      return;
    }

    setIsSending(true);
    setSendResult(null);

    try {
      if (sendMode === 'individual') {
        if (!selectedMember) {
          setSendResult({ type: 'error', message: 'Please select a member' });
          setIsSending(false);
          return;
        }

        const member = members.find(m => m.id === selectedMember);
        if (!member) {
          setSendResult({ type: 'error', message: 'Member not found' });
          setIsSending(false);
          return;
        }

        await apiService.sendIndividualMessage({
          recipient_type: 'member',
          member_id: selectedMember,
          type: channel,
          subject: channel === 'email' || channel === 'push_notification' ? subject : undefined,
          content: messageContent
        });

        setSendResult({ type: 'success', message: 'Message sent successfully!' });
        
        // Reset form
        setSelectedMember(null);
        setSubject('');
        setMessageContent('');
        
        // Refresh members to get latest phone numbers
        await loadMembers();
        
        // Refresh conversations
        await loadConversations();
      } else {
        // Bulk send
        let memberIds: number[] | undefined;
        let groupId: number | undefined;

        if (recipientType === 'selected') {
          if (selectedMembers.length === 0) {
            setSendResult({ type: 'error', message: 'Please select at least one member' });
            setIsSending(false);
            return;
          }
          memberIds = selectedMembers;
        } else if (recipientType === 'group') {
          if (!selectedGroup) {
            setSendResult({ type: 'error', message: 'Please select a group' });
            setIsSending(false);
            return;
          }
          groupId = selectedGroup;
        }

        const result = await apiService.sendBulkMessage({
          type: channel,
          content: messageContent,
          subject: channel === 'email' || channel === 'push_notification' ? subject : undefined,
          recipient_type: recipientType === 'selected' ? 'selected' : recipientType === 'group' ? 'group' : 'all_members',
          member_ids: memberIds,
          group_id: groupId
        });

        setSendResult({
          type: 'success',
          message: `Bulk message sent! ${result.sent} successful, ${result.failed} failed.`
        });

        // Reset form
        setSelectedMembers([]);
        setSelectedGroup(null);
        setSubject('');
        setMessageContent('');

        // Refresh conversations
        await loadConversations();
      }
    } catch (error: any) {
      setSendResult({
        type: 'error',
        message: error.response?.data?.error || error.message || 'Failed to send message'
      });
    } finally {
      setIsSending(false);
    }
  };

  // Handle reply
  const handleReply = async () => {
    if (!selectedConversation || !replyContent.trim()) {
      return;
    }

    setIsReplying(true);

    try {
      const conversationChannel = selectedConversation.channel || 'email';
      if (conversationChannel !== 'sms' && conversationChannel !== 'email' && conversationChannel !== 'push_notification') {
        setSendResult({ type: 'error', message: 'Can only reply to SMS, Email, or Push Notification conversations' });
        setIsReplying(false);
        return;
      }

      await apiService.replyToConversation(selectedConversation.id, {
        type: conversationChannel as 'email' | 'sms' | 'push_notification',
        content: replyContent,
        subject: conversationChannel === 'email' || conversationChannel === 'push_notification' ? (subject || `Re: ${selectedConversation.last_message?.substring(0, 50)}...`) : undefined
      });

      setReplyContent('');
      setSubject('');
      await loadMessages(selectedConversation.id);
      await loadConversations();
    } catch (error: any) {
      setSendResult({
        type: 'error',
        message: error.response?.data?.error || error.message || 'Failed to send reply'
      });
    } finally {
      setIsReplying(false);
    }
  };

  // Format date/time
  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get recipient count for bulk
  const getRecipientCount = () => {
    if (recipientType === 'all_members') {
      if (channel === 'push_notification') {
        return members.length; // Push notifications check for device tokens on backend
      }
      return members.filter(m => channel === 'sms' ? m.phone : m.email).length;
    } else if (recipientType === 'group' && selectedGroup) {
      const group = groups.find(g => g.id === selectedGroup);
      return group?.member_count || 0;
    } else if (recipientType === 'selected') {
      return selectedMembers.length;
    }
    return 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className={clsx(
          "transition-colors",
          theme === 'dark' ? "text-white" : "text-gray-900"
        )}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className={clsx(
          "text-4xl font-bold bg-clip-text text-transparent",
          theme === 'dark'
            ? "bg-gradient-to-r from-white to-gray-300"
            : "bg-gradient-to-r from-gray-900 to-gray-700"
        )}>
          Communication
        </h1>
        <p className={clsx(
          "mt-3 text-lg transition-colors",
          theme === 'dark' ? "text-gray-300" : "text-gray-600"
        )}>
          Send messages and manage conversations with members
        </p>
      </div>

        {/* Main Tabs */}
        <TabNavigation
          tabs={[
            {
              id: 'send',
              label: 'Send Message',
              icon: <PaperAirplaneIcon className="h-5 w-5" />
            },
            {
              id: 'history',
              label: 'Message History',
              icon: <ChatBubbleLeftRightIcon className="h-5 w-5" />
            },
            {
              id: 'campaigns',
              label: 'Campaigns',
              icon: <SparklesIcon className="h-5 w-5" />
            }
          ]}
          activeTab={activeTab}
          onTabChange={(tabId) => {
            setActiveTab(tabId as 'send' | 'history' | 'campaigns');
            setSelectedConversation(null);
          }}
        />

      {/* Send Message Tab */}
      {activeTab === 'send' && (
        <div className={clsx(
          "backdrop-blur-lg rounded-2xl shadow-2xl border p-8 transition-colors",
          theme === 'dark'
            ? "bg-white/10 border-white/20"
            : "bg-white/80 border-gray-200/50"
        )}>
            {/* Mode Toggle */}
            <div className="mb-6">
              <div className="flex space-x-4">
                <button
                  onClick={() => setSendMode('individual')}
                  className={clsx(
                    "px-6 py-3 rounded-lg font-medium transition-all",
                    sendMode === 'individual'
                      ? 'bg-gradient-to-r from-orange-500/20 to-blue-500/20 text-white shadow-lg backdrop-blur-sm border border-orange-400/30'
                      : theme === 'dark'
                        ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  Individual
                </button>
                <button
                  onClick={() => setSendMode('bulk')}
                  className={clsx(
                    "px-6 py-3 rounded-lg font-medium transition-all",
                    sendMode === 'bulk'
                      ? 'bg-gradient-to-r from-orange-500/20 to-blue-500/20 text-white shadow-lg backdrop-blur-sm border border-orange-400/30'
                      : theme === 'dark'
                        ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  Bulk
                </button>
              </div>
            </div>

            {/* Channel Toggle */}
            <div className="mb-6">
              <div className="flex space-x-4">
                <button
                  onClick={() => setChannel('email')}
                  className={clsx(
                    "flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all",
                    channel === 'email'
                      ? 'bg-gradient-to-r from-orange-500/20 to-blue-500/20 text-white shadow-lg backdrop-blur-sm border border-orange-400/30'
                      : theme === 'dark'
                        ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  <EnvelopeIcon className="h-5 w-5" />
                  <span>Email</span>
                </button>
                <button
                  onClick={() => setChannel('sms')}
                  className={clsx(
                    "flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all",
                    channel === 'sms'
                      ? 'bg-gradient-to-r from-orange-500/20 to-blue-500/20 text-white shadow-lg backdrop-blur-sm border border-orange-400/30'
                      : theme === 'dark'
                        ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  <DevicePhoneMobileIcon className="h-5 w-5" />
                  <span>SMS</span>
                </button>
                <button
                  onClick={() => setChannel('push_notification')}
                  className={clsx(
                    "flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all",
                    channel === 'push_notification'
                      ? 'bg-gradient-to-r from-orange-500/20 to-blue-500/20 text-white shadow-lg backdrop-blur-sm border border-orange-400/30'
                      : theme === 'dark'
                        ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  <BellIcon className="h-5 w-5" />
                  <span>Push Notification</span>
                </button>
              </div>
            </div>

            {/* Recipient Selection */}
            {sendMode === 'individual' ? (
              <div className="mb-6">
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>
                  Select Member *
                </label>
                <select
                  value={selectedMember || ''}
                  onChange={(e) => setSelectedMember(e.target.value ? parseInt(e.target.value) : null)}
                  className={clsx(
                    "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-colors",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  )}
                >
                  <option value="">Choose a member...</option>
                  {members
                    .filter(m => {
                      if (channel === 'push_notification') return true; // Show all members, backend checks for device tokens
                      return channel === 'sms' ? m.phone : m.email;
                    })
                    .map(member => (
                      <option key={member.id} value={member.id}>
                        {member.first_name} {member.last_name} ({channel === 'sms' ? member.phone : channel === 'email' ? member.email : 'Push Notification'})
                      </option>
                    ))}
                </select>
              </div>
            ) : (
              <div className="mb-6 space-y-4">
                <div>
                  <label className={clsx(
                    "block text-sm font-medium mb-2 transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>
                    Recipient Type *
                  </label>
                  <select
                    value={recipientType}
                    onChange={(e) => {
                      setRecipientType(e.target.value as 'all_members' | 'group' | 'selected');
                      setSelectedMembers([]);
                      setSelectedGroup(null);
                    }}
                    className={clsx(
                      "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-colors",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    )}
                  >
                    <option value="all_members">All Members</option>
                    <option value="group">Group</option>
                    <option value="selected">Selected Members</option>
                  </select>
                </div>

                {recipientType === 'group' && (
                  <div>
                    <label className={clsx(
                      "block text-sm font-medium mb-2 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-700"
                    )}>
                      Select Group *
                    </label>
                    <select
                      value={selectedGroup || ''}
                      onChange={(e) => setSelectedGroup(e.target.value ? parseInt(e.target.value) : null)}
                      className={clsx(
                        "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-colors",
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      )}
                    >
                      <option value="">Choose a group...</option>
                      {groups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name} ({group.member_count || 0} members)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {recipientType === 'selected' && (
                  <div>
                    <label className={clsx(
                      "block text-sm font-medium mb-2 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-700"
                    )}>
                      Select Members *
                    </label>
                    <select
                      multiple
                      value={selectedMembers.map(String)}
                      onChange={(e) => {
                        const values = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                        setSelectedMembers(values);
                      }}
                      className={clsx(
                        "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 min-h-[120px] transition-colors",
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      )}
                    >
                      {members
                        .filter(m => {
                          if (channel === 'push_notification') return true; // Show all members, backend checks for device tokens
                          return channel === 'sms' ? m.phone : m.email;
                        })
                        .map(member => (
                          <option key={member.id} value={member.id}>
                            {member.first_name} {member.last_name} ({channel === 'sms' ? member.phone : channel === 'email' ? member.email : 'Push Notification'})
                          </option>
                        ))}
                    </select>
                    <p className={clsx(
                      "mt-2 text-sm transition-colors",
                      theme === 'dark' ? "text-gray-400" : "text-gray-500"
                    )}>Hold Ctrl/Cmd to select multiple members</p>
                  </div>
                )}

                <div className="bg-gradient-to-r from-orange-500/20 to-blue-500/20 border border-orange-400/30 rounded-lg p-4">
                  <p className="text-sm text-orange-200">
                    <strong>{getRecipientCount()}</strong> {
                      channel === 'sms' 
                        ? 'members with phone numbers' 
                        : channel === 'email'
                        ? 'members with email addresses'
                        : 'members (only those with push notifications enabled will receive)'
                    } will receive this message
                  </p>
                </div>
              </div>
            )}

            {/* Subject (Email and Push Notifications) */}
            {(channel === 'email' || channel === 'push_notification') && (
              <div className="mb-6">
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>
                  {channel === 'push_notification' ? 'Title' : 'Subject'} *
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={channel === 'push_notification' ? 'Enter notification title...' : 'Enter email subject...'}
                  className={clsx(
                    "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-colors",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  )}
                />
              </div>
            )}

            {/* Message Content */}
            <div className="mb-6">
              <label className={clsx(
                "block text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-700"
              )}>
                Message *
              </label>
              <textarea
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder={
                  channel === 'sms' 
                    ? 'Enter SMS message (160 characters recommended)...' 
                    : channel === 'push_notification'
                    ? 'Enter notification message...'
                    : 'Enter your message...'
                }
                rows={6}
                maxLength={channel === 'sms' ? 1600 : undefined}
                className={clsx(
                  "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 resize-none transition-colors",
                  theme === 'dark'
                    ? "bg-white/10 border-white/20 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                )}
              />
              {channel === 'sms' && (
                <p className={clsx(
                  "mt-2 text-sm transition-colors",
                  theme === 'dark' ? "text-gray-400" : "text-gray-500"
                )}>
                  {messageContent.length} / 1600 characters
                </p>
              )}
            </div>

            {/* Send Button */}
            <button
              onClick={handleSendMessage}
              disabled={isSending || !messageContent.trim()}
              className="w-full bg-gradient-to-r from-orange-500 to-blue-500 hover:from-orange-600 hover:to-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg"
            >
              {isSending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="h-5 w-5" />
                  <span>Send Message</span>
                </>
              )}
            </button>

            {/* Result Message */}
            {sendResult && (
              <div className={clsx(
                "mt-4 p-4 rounded-lg border",
                sendResult.type === 'success' 
                  ? clsx(
                      "bg-green-500/20 border-green-500/50",
                      theme === 'dark' ? "text-green-300" : "text-green-700"
                    )
                  : clsx(
                      "bg-red-500/20 border-red-500/50",
                      theme === 'dark' ? "text-red-300" : "text-red-700"
                    )
              )}>
                {sendResult.message}
              </div>
            )}
          </div>
        )}

      {/* Message History Tab */}
      {activeTab === 'history' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conversations List */}
          <div className={clsx(
            "lg:col-span-1 backdrop-blur-lg rounded-2xl shadow-2xl border overflow-hidden transition-colors",
            theme === 'dark'
              ? "bg-white/10 border-white/20"
              : "bg-white/80 border-gray-200/50"
          )}>
              <div className={clsx(
                "p-4 border-b transition-colors",
                theme === 'dark' ? "border-white/10" : "border-gray-200"
              )}>
                <h2 className={clsx(
                  "text-xl font-semibold transition-colors",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>Conversations</h2>
              </div>
              <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
                {conversations.length === 0 ? (
                  <div className={clsx(
                    "p-4 text-center transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )}>
                    No conversations yet
                  </div>
                ) : (
                  conversations.map(conv => (
                    <button
                      key={conv.id}
                      onClick={() => handleSelectConversation(conv)}
                      className={clsx(
                        "w-full p-4 text-left border-b transition-all",
                        theme === 'dark'
                          ? "border-white/10 hover:bg-white/5"
                          : "border-gray-200 hover:bg-gray-50",
                        selectedConversation?.id === conv.id 
                          ? 'bg-gradient-to-r from-orange-500/20 to-blue-500/20 border-orange-400/30' 
                          : ''
                      )}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className={clsx(
                              "font-semibold transition-colors",
                              theme === 'dark' ? "text-white" : "text-gray-900"
                            )}>
                              {conv.first_name && conv.last_name
                                ? `${conv.first_name} ${conv.last_name}`
                                : conv.phone_number || conv.email_address || 'Unknown'}
                            </span>
                            {conv.channel && (
                              <span className={clsx(
                                "text-xs px-2 py-1 rounded",
                                conv.channel === 'sms' 
                                  ? clsx("bg-blue-500/20", theme === 'dark' ? "text-blue-300" : "text-blue-700")
                                  : conv.channel === 'email'
                                  ? clsx("bg-green-500/20", theme === 'dark' ? "text-green-300" : "text-green-700")
                                  : conv.channel === 'push_notification'
                                  ? clsx("bg-purple-500/20", theme === 'dark' ? "text-purple-300" : "text-purple-700")
                                  : clsx("bg-gray-500/20", theme === 'dark' ? "text-gray-300" : "text-gray-700")
                              )}>
                                {conv.channel === 'push_notification' ? 'PUSH' : conv.channel.toUpperCase()}
                              </span>
                            )}
                          </div>
                          <p className={clsx(
                            "text-sm truncate transition-colors",
                            theme === 'dark' ? "text-gray-400" : "text-gray-500"
                          )}>
                            {conv.last_message || 'No messages yet'}
                          </p>
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="bg-gradient-to-r from-orange-500 to-blue-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <p className={clsx(
                        "text-xs transition-colors",
                        theme === 'dark' ? "text-gray-500" : "text-gray-400"
                      )}>
                        {formatDateTime(conv.last_message_at)}
                      </p>
                    </button>
                  ))
                )}
              </div>
            </div>

          {/* Conversation Thread */}
          <div className={clsx(
            "lg:col-span-2 backdrop-blur-lg rounded-2xl shadow-2xl border overflow-hidden flex flex-col transition-colors",
            theme === 'dark'
              ? "bg-white/10 border-white/20"
              : "bg-white/80 border-gray-200/50"
          )}>
              {selectedConversation ? (
                <>
                  <div className={clsx(
                    "p-4 border-b transition-colors",
                    theme === 'dark' ? "border-white/10" : "border-gray-200"
                  )}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className={clsx(
                          "text-xl font-semibold transition-colors",
                          theme === 'dark' ? "text-white" : "text-gray-900"
                        )}>
                          {selectedConversation.first_name && selectedConversation.last_name
                            ? `${selectedConversation.first_name} ${selectedConversation.last_name}`
                            : selectedConversation.phone_number || selectedConversation.email_address || 'Unknown'}
                        </h2>
                        <p className={clsx(
                          "text-sm transition-colors",
                          theme === 'dark' ? "text-gray-400" : "text-gray-500"
                        )}>
                          {selectedConversation.phone_number || selectedConversation.email_address}
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedConversation(null)}
                        className={clsx(
                          "transition-colors",
                          theme === 'dark' ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"
                        )}
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[calc(100vh-400px)]">
                    {isLoadingMessages ? (
                      <div className={clsx(
                        "text-center transition-colors",
                        theme === 'dark' ? "text-gray-400" : "text-gray-500"
                      )}>Loading messages...</div>
                    ) : messages.length === 0 ? (
                      <div className={clsx(
                        "text-center transition-colors",
                        theme === 'dark' ? "text-gray-400" : "text-gray-500"
                      )}>No messages yet</div>
                    ) : (
                      messages.map(msg => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_type === 'employee' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={clsx(
                              "max-w-[70%] rounded-lg p-4",
                              msg.sender_type === 'employee'
                                ? 'bg-gradient-to-r from-orange-500/30 to-blue-500/30 text-white border border-orange-400/30'
                                : theme === 'dark'
                                  ? 'bg-white/10 text-gray-300'
                                  : 'bg-gray-100 text-gray-900'
                            )}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className={clsx(
                                "text-sm font-semibold transition-colors",
                                msg.sender_type === 'employee' 
                                  ? "text-white"
                                  : theme === 'dark' ? "text-gray-300" : "text-gray-900"
                              )}>{msg.sender_name}</span>
                              <span className={clsx(
                                "text-xs opacity-75 ml-2 transition-colors",
                                msg.sender_type === 'employee'
                                  ? "text-white"
                                  : theme === 'dark' ? "text-gray-400" : "text-gray-500"
                              )}>
                                {formatDateTime(msg.timestamp)}
                              </span>
                            </div>
                            {msg.subject && (
                              <p className={clsx(
                                "text-sm font-medium mb-2 opacity-90 transition-colors",
                                msg.sender_type === 'employee'
                                  ? "text-white"
                                  : theme === 'dark' ? "text-gray-300" : "text-gray-900"
                              )}>
                                {msg.subject}
                              </p>
                            )}
                            <p className={clsx(
                              "text-sm whitespace-pre-wrap transition-colors",
                              msg.sender_type === 'employee'
                                ? "text-white"
                                : theme === 'dark' ? "text-gray-300" : "text-gray-900"
                            )}>{msg.content}</p>
                            {msg.delivery_status && (
                              <p className={clsx(
                                "text-xs mt-2 opacity-75 transition-colors",
                                msg.sender_type === 'employee'
                                  ? "text-white"
                                  : theme === 'dark' ? "text-gray-400" : "text-gray-500"
                              )}>
                                Status: {msg.delivery_status}
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Reply Box */}
                  <div className={clsx(
                    "p-4 border-t transition-colors",
                    theme === 'dark' ? "border-white/10" : "border-gray-200"
                  )}>
                    {(selectedConversation.channel === 'email' || selectedConversation.channel === 'push_notification') && (
                      <input
                        type="text"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Subject (optional)"
                        className={clsx(
                          "w-full mb-2 px-4 py-2 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-colors",
                          theme === 'dark'
                            ? "bg-white/10 border-white/20 text-white placeholder-gray-500"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                        )}
                      />
                    )}
                    <div className="flex space-x-2">
                      <textarea
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        placeholder="Type your reply..."
                        rows={3}
                        className={clsx(
                          "flex-1 px-4 py-2 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 resize-none transition-colors",
                          theme === 'dark'
                            ? "bg-white/10 border-white/20 text-white placeholder-gray-500"
                            : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                        )}
                      />
                      <button
                        onClick={handleReply}
                        disabled={isReplying || !replyContent.trim()}
                        className="bg-gradient-to-r from-orange-500 to-blue-500 hover:from-orange-600 hover:to-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 shadow-lg"
                      >
                        {isReplying ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                          <>
                            <PaperAirplaneIcon className="h-5 w-5" />
                            <span>Send</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className={clsx(
                  "flex-1 flex items-center justify-center transition-colors",
                  theme === 'dark' ? "text-gray-400" : "text-gray-500"
                )}>
                  Select a conversation to view messages
                </div>
              )}
            </div>
        </div>
      )}

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <CampaignsSection />
      )}
    </div>
  );
};

// Campaigns Section Component
const CampaignsSection: React.FC = () => {
  const { theme } = useTheme();
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<any | null>(null);
  const [showExecutions, setShowExecutions] = useState<number | null>(null);

  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    try {
      setIsLoading(true);
      const data = await apiService.getAutomationWorkflows();
      setWorkflows(data);
    } catch (error) {
      console.error('Failed to load workflows:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await apiService.activateAutomationWorkflow(id);
      await loadWorkflows();
    } catch (error) {
      console.error('Failed to activate workflow:', error);
      alert('Failed to activate workflow');
    }
  };

  const handlePause = async (id: number) => {
    try {
      await apiService.pauseAutomationWorkflow(id);
      await loadWorkflows();
    } catch (error) {
      console.error('Failed to pause workflow:', error);
      alert('Failed to pause workflow');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this workflow?')) return;
    try {
      await apiService.deleteAutomationWorkflow(id);
      await loadWorkflows();
    } catch (error) {
      console.error('Failed to delete workflow:', error);
      alert('Failed to delete workflow');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className={clsx(
            "text-2xl font-bold transition-colors",
            theme === 'dark' ? "text-white" : "text-gray-900"
          )}>Automated Campaigns</h2>
          <p className={clsx(
            "mt-2 transition-colors",
            theme === 'dark' ? "text-gray-300" : "text-gray-600"
          )}>
            Create automated workflows that trigger based on member actions
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedWorkflow(null);
            setShowCreateModal(true);
          }}
          className="px-6 py-3 bg-gradient-to-r from-orange-600 to-blue-600 hover:from-orange-700 hover:to-blue-700 text-white font-medium rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Create Campaign</span>
        </button>
      </div>

      {workflows.length === 0 ? (
        <div className={clsx(
          "backdrop-blur-lg rounded-2xl shadow-2xl border p-12 text-center transition-colors",
          theme === 'dark'
            ? "bg-white/10 border-white/20"
            : "bg-white/80 border-gray-200/50"
        )}>
          <SparklesIcon className={clsx(
            "h-16 w-16 mx-auto mb-4 transition-colors",
            theme === 'dark' ? "text-gray-400" : "text-gray-500"
          )} />
          <h3 className={clsx(
            "text-xl font-bold mb-2 transition-colors",
            theme === 'dark' ? "text-white" : "text-gray-900"
          )}>No Campaigns Yet</h3>
          <p className={clsx(
            "mb-6 transition-colors",
            theme === 'dark' ? "text-gray-300" : "text-gray-600"
          )}>
            Create your first automated campaign to engage members automatically
          </p>
          <button
            onClick={() => {
              setSelectedWorkflow(null);
              setShowCreateModal(true);
            }}
            className="px-6 py-3 bg-gradient-to-r from-orange-600 to-blue-600 hover:from-orange-700 hover:to-blue-700 text-white font-medium rounded-xl transition-all duration-200"
          >
            Create Your First Campaign
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {workflows.map((workflow) => (
            <div
              key={workflow.id}
              className={clsx(
                "backdrop-blur-lg rounded-2xl shadow-2xl border p-6 transition-colors",
                theme === 'dark'
                  ? "bg-white/10 border-white/20"
                  : "bg-white/80 border-gray-200/50"
              )}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className={clsx(
                    "text-xl font-bold transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>{workflow.name}</h3>
                  {workflow.description && (
                    <p className={clsx(
                      "mt-1 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-600"
                    )}>{workflow.description}</p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <span
                    className={clsx(
                      "px-3 py-1 rounded-full text-sm font-medium",
                      workflow.status === 'active'
                        ? clsx("bg-green-500/20", theme === 'dark' ? "text-green-300" : "text-green-700")
                        : workflow.status === 'paused'
                        ? clsx("bg-yellow-500/20", theme === 'dark' ? "text-yellow-300" : "text-yellow-700")
                        : clsx("bg-gray-500/20", theme === 'dark' ? "text-gray-300" : "text-gray-700")
                    )}
                  >
                    {workflow.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className={clsx(
                    "text-sm transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )}>Trigger</p>
                  <p className={clsx(
                    "font-medium capitalize transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>
                    {workflow.trigger_type.replace('_', ' ')}
                  </p>
                </div>
                <div>
                  <p className={clsx(
                    "text-sm transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )}>Steps</p>
                  <p className={clsx(
                    "font-medium transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>{workflow.steps?.length || 0}</p>
                </div>
                <div>
                  <p className={clsx(
                    "text-sm transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )}>Executions</p>
                  <p className={clsx(
                    "font-medium transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>{workflow.total_executions || 0}</p>
                </div>
                <div>
                  <p className={clsx(
                    "text-sm transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )}>Created</p>
                  <p className={clsx(
                    "font-medium transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>
                    {new Date(workflow.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className={clsx(
                "flex items-center justify-between pt-4 border-t transition-colors",
                theme === 'dark' ? "border-white/10" : "border-gray-200"
              )}>
                <div className="flex items-center space-x-2">
                  {workflow.status === 'active' ? (
                    <button
                      onClick={() => handlePause(workflow.id)}
                      className={clsx(
                        "px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 rounded-lg transition-colors",
                        theme === 'dark' ? "text-yellow-300" : "text-yellow-700"
                      )}
                    >
                      Pause
                    </button>
                  ) : workflow.status === 'paused' ? (
                    <button
                      onClick={() => handleActivate(workflow.id)}
                      className={clsx(
                        "px-4 py-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg transition-colors",
                        theme === 'dark' ? "text-green-300" : "text-green-700"
                      )}
                    >
                      Activate
                    </button>
                  ) : (
                    <button
                      onClick={() => handleActivate(workflow.id)}
                      className={clsx(
                        "px-4 py-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg transition-colors",
                        theme === 'dark' ? "text-green-300" : "text-green-700"
                      )}
                    >
                      Activate
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setSelectedWorkflow(workflow);
                      setShowCreateModal(true);
                    }}
                    className={clsx(
                      "px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors",
                      theme === 'dark' ? "text-blue-300" : "text-blue-700"
                    )}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setShowExecutions(showExecutions === workflow.id ? null : workflow.id)}
                    className={clsx(
                      "px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg transition-colors",
                      theme === 'dark' ? "text-purple-300" : "text-purple-700"
                    )}
                  >
                    View Executions
                  </button>
                </div>
                <button
                  onClick={() => handleDelete(workflow.id)}
                  className={clsx(
                    "px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors",
                    theme === 'dark' ? "text-red-300" : "text-red-700"
                  )}
                >
                  Delete
                </button>
              </div>

              {showExecutions === workflow.id && (
                <WorkflowExecutions workflowId={workflow.id} />
              )}
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <WorkflowBuilderModal
          workflow={selectedWorkflow}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedWorkflow(null);
          }}
          onSave={async () => {
            await loadWorkflows();
            setShowCreateModal(false);
            setSelectedWorkflow(null);
          }}
        />
      )}
    </div>
  );
};

// Workflow Executions Component
const WorkflowExecutions: React.FC<{ workflowId: number }> = ({ workflowId }) => {
  const { theme } = useTheme();
  const [executions, setExecutions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadExecutions = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiService.getAutomationWorkflowExecutions(workflowId);
      setExecutions(data);
    } catch (error) {
      console.error('Failed to load executions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [workflowId]);

  useEffect(() => {
    loadExecutions();
  }, [loadExecutions]);

  if (isLoading) {
    return (
      <div className={clsx(
        "mt-4 p-4 rounded-lg transition-colors",
        theme === 'dark' ? "bg-white/5" : "bg-gray-100/50"
      )}>
        <div className={clsx(
          "animate-pulse transition-colors",
          theme === 'dark' ? "text-gray-400" : "text-gray-500"
        )}>Loading executions...</div>
      </div>
    );
  }

  if (executions.length === 0) {
    return (
      <div className={clsx(
        "mt-4 p-4 rounded-lg text-center transition-colors",
        theme === 'dark' ? "bg-white/5 text-gray-400" : "bg-gray-100/50 text-gray-500"
      )}>
        No executions yet
      </div>
    );
  }

  return (
    <div className={clsx(
      "mt-4 p-4 rounded-lg transition-colors",
      theme === 'dark' ? "bg-white/5" : "bg-gray-100/50"
    )}>
      <h4 className={clsx(
        "font-medium mb-3 transition-colors",
        theme === 'dark' ? "text-white" : "text-gray-900"
      )}>Recent Executions</h4>
      <div className="space-y-2">
        {executions.slice(0, 5).map((exec) => (
          <div key={exec.id} className={clsx(
            "flex justify-between items-center p-2 rounded transition-colors",
            theme === 'dark' ? "bg-white/5" : "bg-white/50"
          )}>
            <div>
              <p className={clsx(
                "text-sm transition-colors",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>{exec.member_name || 'Unknown Member'}</p>
              <p className={clsx(
                "text-xs transition-colors",
                theme === 'dark' ? "text-gray-400" : "text-gray-500"
              )}>
                {new Date(exec.started_at).toLocaleString()}
              </p>
            </div>
            <span
              className={clsx(
                "px-2 py-1 rounded text-xs",
                exec.status === 'completed'
                  ? clsx("bg-green-500/20", theme === 'dark' ? "text-green-300" : "text-green-700")
                  : exec.status === 'running'
                  ? clsx("bg-blue-500/20", theme === 'dark' ? "text-blue-300" : "text-blue-700")
                  : clsx("bg-gray-500/20", theme === 'dark' ? "text-gray-300" : "text-gray-700")
              )}
            >
              {exec.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Workflow Builder Modal Component (simplified version - full implementation would be more complex)
const WorkflowBuilderModal: React.FC<{
  workflow?: any;
  onClose: () => void;
  onSave: () => void;
}> = ({ workflow, onClose, onSave }) => {
  const { theme } = useTheme();
  const [name, setName] = useState(workflow?.name || '');
  const [description, setDescription] = useState(workflow?.description || '');
  const [triggerType, setTriggerType] = useState(workflow?.trigger_type || 'member_signup');
  const [steps, setSteps] = useState<any[]>(workflow?.steps || []);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Please enter a workflow name');
      return;
    }
    if (steps.length === 0) {
      alert('Please add at least one step');
      return;
    }

    try {
      setIsSaving(true);
      const workflowData = {
        name,
        description,
        trigger_type: triggerType,
        steps: steps.map((step, index) => ({
          ...step,
          step_order: index + 1
        }))
      };

      if (workflow) {
        await apiService.updateAutomationWorkflow(workflow.id, workflowData);
      } else {
        await apiService.createAutomationWorkflow(workflowData);
      }
      onSave();
    } catch (error) {
      console.error('Failed to save workflow:', error);
      alert('Failed to save workflow');
    } finally {
      setIsSaving(false);
    }
  };

  const addStep = () => {
    setSteps([
      ...steps,
      {
        action_type: 'send_email',
        step_order: steps.length + 1,
        delay_days: 0,
        delay_hours: 0,
        is_active: true
      }
    ]);
  };

  const removeStep = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index));
  };

  const updateStep = (index: number, updates: any) => {
    setSteps(steps.map((step, i) => (i === index ? { ...step, ...updates } : step)));
  };

  return (
    <div className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={clsx(
        "rounded-2xl border shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto transition-colors",
        theme === 'dark'
          ? "bg-gradient-to-br from-slate-800 to-slate-900 border-white/20"
          : "bg-white border-gray-200"
      )}>
        <div className={clsx(
          "flex items-center justify-between p-6 border-b transition-colors",
          theme === 'dark' ? "border-white/20" : "border-gray-200"
        )}>
          <h2 className={clsx(
            "text-2xl font-bold transition-colors",
            theme === 'dark' ? "text-white" : "text-gray-900"
          )}>
            {workflow ? 'Edit Campaign' : 'Create Campaign'}
          </h2>
          <button
            onClick={onClose}
            className={clsx(
              "transition-colors",
              theme === 'dark' ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"
            )}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className={clsx(
              "block text-sm font-medium mb-2 transition-colors",
              theme === 'dark' ? "text-gray-300" : "text-gray-700"
            )}>
              Campaign Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={clsx(
                "w-full px-4 py-2 border rounded-lg transition-colors",
                theme === 'dark'
                  ? "bg-white/10 border-white/20 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              )}
              placeholder="e.g., Welcome New Members"
            />
          </div>

          <div>
            <label className={clsx(
              "block text-sm font-medium mb-2 transition-colors",
              theme === 'dark' ? "text-gray-300" : "text-gray-700"
            )}>
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={clsx(
                "w-full px-4 py-2 border rounded-lg transition-colors",
                theme === 'dark'
                  ? "bg-white/10 border-white/20 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              )}
              rows={2}
              placeholder="Describe what this campaign does..."
            />
          </div>

          <div>
            <label className={clsx(
              "block text-sm font-medium mb-2 transition-colors",
              theme === 'dark' ? "text-gray-300" : "text-gray-700"
            )}>
              Trigger When
            </label>
            <select
              value={triggerType}
              onChange={(e) => setTriggerType(e.target.value)}
              className={clsx(
                "w-full px-4 py-2 border rounded-lg transition-colors",
                theme === 'dark'
                  ? "bg-white/10 border-white/20 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              )}
            >
              <option value="member_signup">Member Signs Up</option>
              <option value="member_inactive">Member Becomes Inactive</option>
              <option value="workout_completed">Member Completes Workout</option>
              <option value="class_booked">Member Books Class</option>
              <option value="class_missed">Member Misses Class</option>
            </select>
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
              <label className={clsx(
                "block text-sm font-medium transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-700"
              )}>
                Campaign Steps
              </label>
              <button
                onClick={addStep}
                className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg transition-colors text-sm"
              >
                + Add Step
              </button>
            </div>

            <div className="space-y-4">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={clsx(
                    "p-4 rounded-lg border transition-colors",
                    theme === 'dark'
                      ? "bg-white/5 border-white/10"
                      : "bg-gray-50 border-gray-200"
                  )}
                >
                  <div className="flex justify-between items-start mb-3">
                    <span className={clsx(
                      "font-medium transition-colors",
                      theme === 'dark' ? "text-white" : "text-gray-900"
                    )}>Step {index + 1}</span>
                    <button
                      onClick={() => removeStep(index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={clsx(
                        "block text-xs mb-1 transition-colors",
                        theme === 'dark' ? "text-gray-400" : "text-gray-500"
                      )}>Action</label>
                      <select
                        value={step.action_type}
                        onChange={(e) => updateStep(index, { action_type: e.target.value })}
                        className={clsx(
                          "w-full px-3 py-2 border rounded text-sm transition-colors",
                          theme === 'dark'
                            ? "bg-white/10 border-white/20 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        )}
                      >
                        <option value="send_email">Send Email</option>
                        <option value="send_sms">Send SMS</option>
                        <option value="send_push">Send Push Notification</option>
                        <option value="wait">Wait</option>
                      </select>
                    </div>

                    <div>
                      <label className={clsx(
                        "block text-xs mb-1 transition-colors",
                        theme === 'dark' ? "text-gray-400" : "text-gray-500"
                      )}>Delay (Days)</label>
                      <input
                        type="number"
                        value={step.delay_days || 0}
                        onChange={(e) =>
                          updateStep(index, { delay_days: parseInt(e.target.value) || 0 })
                        }
                        className={clsx(
                          "w-full px-3 py-2 border rounded text-sm transition-colors",
                          theme === 'dark'
                            ? "bg-white/10 border-white/20 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        )}
                        min="0"
                      />
                    </div>
                  </div>

                  {(step.action_type === 'send_email' ||
                    step.action_type === 'send_sms' ||
                    step.action_type === 'send_push') && (
                    <div className="mt-3">
                      <label className={clsx(
                        "block text-xs mb-1 transition-colors",
                        theme === 'dark' ? "text-gray-400" : "text-gray-500"
                      )}>Message Content</label>
                      <textarea
                        value={step.custom_content || ''}
                        onChange={(e) => updateStep(index, { custom_content: e.target.value })}
                        className={clsx(
                          "w-full px-3 py-2 border rounded text-sm transition-colors",
                          theme === 'dark'
                            ? "bg-white/10 border-white/20 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        )}
                        rows={3}
                        placeholder="Enter your message here..."
                      />
                    </div>
                  )}

                  {step.action_type === 'send_email' && (
                    <div className="mt-3">
                      <label className={clsx(
                        "block text-xs mb-1 transition-colors",
                        theme === 'dark' ? "text-gray-400" : "text-gray-500"
                      )}>Subject</label>
                      <input
                        type="text"
                        value={step.subject || ''}
                        onChange={(e) => updateStep(index, { subject: e.target.value })}
                        className={clsx(
                          "w-full px-3 py-2 border rounded text-sm transition-colors",
                          theme === 'dark'
                            ? "bg-white/10 border-white/20 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        )}
                        placeholder="Email subject..."
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={clsx(
          "flex justify-end space-x-3 p-6 border-t transition-colors",
          theme === 'dark' ? "border-white/20" : "border-gray-200"
        )}>
          <button
            onClick={onClose}
            className={clsx(
              "px-6 py-2 transition-colors",
              theme === 'dark' ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"
            )}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2 bg-gradient-to-r from-orange-600 to-blue-600 hover:from-orange-700 hover:to-blue-700 text-white rounded-lg transition-all duration-200 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Campaign'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommunicationPage;
